<template>
    <modal :open="open" size="lg" @close="close">
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            {{ title }}
        </h3>
        <p v-if="description" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{ description }}
        </p>

        <div class="mt-4">
            <span class="block text-sm font-medium text-gray-700 dark:text-white">Experiência</span>
            <div class="mt-2 space-y-2">
                <label
                    v-for="option in options"
                    :key="String(option.value)"
                    class="flex cursor-pointer items-start gap-2 rounded-md border border-gray-200 px-3 py-2 dark:border-gray-700"
                    :class="
                        value === option.value
                            ? 'border-indigo-500 bg-indigo-500/5 dark:border-indigo-400'
                            : ''
                    "
                >
                    <input
                        type="radio"
                        class="mt-0.5 h-4 w-4 text-indigo-600"
                        :value="option.value"
                        :checked="value === option.value"
                        :disabled="option.disabled === true"
                        @change="value = option.value"
                    />
                    <span>
                        <span class="block text-sm text-gray-900 dark:text-white">
                            {{ option.label }}
                        </span>
                        <span
                            v-if="option.help"
                            class="block text-xs text-gray-500 dark:text-gray-400"
                        >
                            {{ option.help }}
                        </span>
                    </span>
                </label>
            </div>
        </div>

        <!-- Alcance da mudança: o pai descreve o que acontece com o valor escolhido. -->
        <div
            v-if="$slots.scope"
            class="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
        >
            <slot name="scope" :value="value"></slot>
        </div>

        <div class="mt-4">
            <label
                class="block text-sm font-medium text-gray-700 dark:text-white"
                for="experience-change-reason"
            >
                Motivo da mudança
            </label>
            <!-- Sem maxlength: o motivo longo precisa ser digitado para receber o aviso, nunca truncado. -->
            <textarea
                id="experience-change-reason"
                v-model="reason"
                rows="3"
                class="text-input-sm"
                placeholder="Por que esta mudança está sendo feita?"
            ></textarea>
            <div class="mt-1 flex items-center justify-between">
                <span v-if="error" class="text-xs text-red-600 dark:text-red-400">
                    {{ error }}
                </span>
                <span v-else></span>
                <span
                    class="text-xs"
                    :class="
                        reason.length > reasonMaxLength
                            ? 'font-medium text-red-600 dark:text-red-400'
                            : 'text-gray-500 dark:text-gray-400'
                    "
                >
                    {{ reason.length }}/{{ reasonMaxLength }}
                </span>
            </div>
        </div>

        <div class="mt-5 flex justify-end gap-2">
            <button
                type="button"
                class="rounded-md border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                :disabled="submitting"
                @click="close"
            >
                Cancelar
            </button>
            <button
                type="button"
                class="rounded-md bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                :disabled="busy"
                @click="confirm"
            >
                {{ busy ? "Salvando..." : confirmLabel }}
            </button>
        </div>
    </modal>
</template>

<script>
import Modal from "../../layouts/Modal.vue";

// Mesmo limite do UpdateExperienceTargetRequest / UpdateExperienceSwitchRequest (C17).
const REASON_MAX_LENGTH = 500;

export default {
    name: "ExperienceChangeModal",

    components: { Modal },

    props: {
        open: { type: Boolean, default: false },
        title: { type: String, required: true },
        description: { type: String, default: "" },
        options: { type: Array, required: true },
        currentValue: { default: null },
        submitting: { type: Boolean, default: false },
        confirmLabel: { type: String, default: "Confirmar mudança" },
    },

    emits: ["confirm", "close"],

    data() {
        return {
            value: this.currentValue,
            reason: "",
            error: "",
            // A prop `submitting` só chega no tick seguinte ao emit; este flag fecha
            // a janela em que dois cliques no mesmo tick virariam duas mudanças.
            pending: false,
            reasonMaxLength: REASON_MAX_LENGTH,
        };
    },

    computed: {
        busy() {
            return this.submitting || this.pending;
        },
    },

    watch: {
        open(open) {
            if (open) {
                this.value = this.currentValue;
                this.reason = "";
                this.error = "";
                this.pending = false;
            }
        },
        submitting(submitting) {
            if (!submitting) {
                this.pending = false;
            }
        },
    },

    methods: {
        confirm() {
            if (this.busy) {
                return;
            }

            const reason = this.reason.trim();

            if (reason === "") {
                this.error = "O motivo da mudança é obrigatório.";
                return;
            }

            if (reason.length > REASON_MAX_LENGTH) {
                this.error =
                    "O motivo da mudança deve ter no máximo " +
                    REASON_MAX_LENGTH +
                    " caracteres. Agora tem " +
                    reason.length +
                    ".";
                return;
            }

            this.error = "";
            this.pending = true;
            this.$emit("confirm", { value: this.value, reason });
        },

        close() {
            if (this.busy) {
                return;
            }

            this.$emit("close");
        },
    },
};
</script>
